import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { useT } from '../../i18n';

// Business analytics tab: visitor → preview → purchase funnel, engagement
// rankings, and a daily income-vs-spend / profit view. Spend = per-order COGS
// (printing + delivery + actual tracked AI spend) plus business expenses
// amortized to a daily figure. See server GET /api/admin/analytics.

// Local calendar day as YYYY-MM-DD (the date inputs + the "today" preset speak
// the admin's local date; the server buckets by UTC day, close enough here).
function todayStr() {
  return new Date().toLocaleDateString('en-CA');
}

// cents (ILS) → "123₪". Handles negatives (losses) gracefully.
function money(cents, currency) {
  const v = (cents || 0) / 100;
  const rounded = Math.round(v);
  return `${rounded < 0 ? '−' : ''}${Math.abs(rounded).toLocaleString('en')}${currency}`;
}

function fmtDay(d) {
  try {
    return new Date(d).toLocaleDateString('ar', { day: 'numeric', month: 'short' });
  } catch {
    return String(d).slice(0, 10);
  }
}

const EMPTY_EXPENSE = { label: '', category: 'ads', kind: 'period', amount: '', date: '', startDate: '', endDate: '', note: '' };

export default function AdminStats() {
  const t = useT();

  // Friendly labels for the per-banner promo-click breakdown. Falls back
  // to the event's stored name/slug for any banner not listed here.
  const BANNER_LABELS = {
    'new-product-promo': t.adminBannerGradSeries,
    'promo-banner': t.adminBannerPromo,
  };
  function bannerLabel(slug, name) {
    return BANNER_LABELS[slug] || name || slug || '—';
  }

  // Range presets. 'today' and 'custom' send an explicit from/to window to the
  // analytics endpoint; the rest send a rolling ?days=N. ('all' = days:0.)
  const RANGES = [
    { key: 'today', label: t.adminRangeToday },
    { key: '7', label: t.adminRange7Days, days: 7 },
    { key: '30', label: t.adminRange30Days, days: 30 },
    { key: '90', label: t.adminRange90Days, days: 90 },
    { key: 'all', label: t.adminRangeAll, days: 0 },
    { key: 'custom', label: t.adminRangeCustom },
  ];

  const EXPENSE_CATEGORIES = [
    { value: 'ads', label: t.adminExpenseAds },
    { value: 'ai', label: t.adminExpenseAi },
    { value: 'printing', label: t.adminExpensePrinting },
    { value: 'delivery', label: t.adminExpenseDelivery },
    { value: 'other', label: t.adminExpenseOther },
  ];
  const CATEGORY_AR = Object.fromEntries(EXPENSE_CATEGORIES.map((c) => [c.value, c.label]));

  const [rangeKey, setRangeKey] = useState('30');
  // Custom range bounds (only used when rangeKey === 'custom'). Default the
  // start a week back so the inputs aren't empty when the admin opens custom.
  const [custom, setCustom] = useState({ from: '', to: todayStr() });
  const [showTest, setShowTest] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const [expenses, setExpenses] = useState([]);
  const [form, setForm] = useState(EMPTY_EXPENSE);
  const [savingExpense, setSavingExpense] = useState(false);
  const [expenseError, setExpenseError] = useState(null);

  // COGS rates editor (settings.costs + settings.delivery).
  const [rates, setRates] = useState(null); // { printingCents, deliveryCents, aiUsdToIls }
  const [savingRates, setSavingRates] = useState(false);
  const [ratesMsg, setRatesMsg] = useState(null);

  const loadAnalytics = useCallback(() => {
    const params = new URLSearchParams();
    if (showTest) params.set('includeTest', 'true');
    if (rangeKey === 'today') {
      const d = todayStr();
      params.set('from', d);
      params.set('to', d);
    } else if (rangeKey === 'custom') {
      // Wait until both ends are picked before hitting the server.
      if (!custom.from || !custom.to) return;
      params.set('from', custom.from);
      params.set('to', custom.to);
    } else {
      const r = RANGES.find((x) => x.key === rangeKey);
      params.set('days', String(r?.days ?? 30));
    }
    setError(null);
    api.get(`/api/admin/analytics?${params.toString()}`).then(setData).catch((e) => setError(e.message));
  }, [rangeKey, custom, showTest]);

  const loadExpenses = useCallback(() => {
    api.get('/api/admin/expenses').then(setExpenses).catch(() => {});
  }, []);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);
  useEffect(() => { loadExpenses(); }, [loadExpenses]);

  // Seed the rate editor from settings once.
  useEffect(() => {
    api.get('/api/admin/settings').then((s) => {
      setRates({
        printingCents: s.costs?.printingCents ?? 5000,
        deliveryCents: s.delivery?.amountCents ?? 5000,
        aiUsdToIls: s.costs?.aiUsdToIls ?? 3.7,
      });
    }).catch(() => {});
  }, []);

  const saveRates = async () => {
    if (!rates) return;
    setSavingRates(true);
    setRatesMsg(null);
    try {
      await api.put('/api/admin/settings', {
        costs: { printingCents: Number(rates.printingCents), aiUsdToIls: Number(rates.aiUsdToIls) },
        delivery: { amountCents: Number(rates.deliveryCents), currency: 'ILS' },
      });
      setRatesMsg(t.adminSaved);
      loadAnalytics();
    } catch (e) {
      setRatesMsg(e.message || t.adminSaveFailed);
    } finally {
      setSavingRates(false);
    }
  };

  const submitExpense = async (e) => {
    e.preventDefault();
    setSavingExpense(true);
    setExpenseError(null);
    try {
      const body = {
        label: form.label,
        category: form.category,
        kind: form.kind,
        amountCents: Math.round(Number(form.amount) * 100),
        note: form.note,
      };
      if (form.kind === 'one_time') body.date = form.date;
      else { body.startDate = form.startDate; body.endDate = form.endDate; }
      await api.post('/api/admin/expenses', body);
      setForm(EMPTY_EXPENSE);
      loadExpenses();
      loadAnalytics();
    } catch (err) {
      setExpenseError(err.message || t.adminSaveFailed);
    } finally {
      setSavingExpense(false);
    }
  };

  const deleteExpense = async (id) => {
    try {
      await api.delete(`/api/admin/expenses/${id}`);
      loadExpenses();
      loadAnalytics();
    } catch { /* ignore */ }
  };

  if (error) return <div className="admin-page"><p className="admin-modal-error">{error}</p></div>;
  if (!data) return <div className="loader"><div className="loader-spinner" /></div>;

  const { funnel, financial, topTemplatesViewed, topTemplatesPreviewed, topCategories } = data;
  const tot = financial.totals;
  // Scale the daily bars to the largest income/spend day in the window.
  const maxDay = financial.dailySeries.reduce(
    (m, d) => Math.max(m, d.incomeCents, d.cogsCents + d.expenseCents), 0) || 1;
  const activeDays = financial.dailySeries.filter((d) => d.incomeCents || d.cogsCents || d.expenseCents);

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h1 className="page-title">{t.adminStatistics}</h1>
        <Link to="/admin" className="admin-link" style={{ color: '#6366f1' }}>← {t.adminDashboardLink}</Link>
      </div>

      {/* Range + test toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 14, marginBottom: custom && rangeKey === 'custom' ? 10 : 22 }}>
        <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              className={rangeKey === r.key ? 'admin-btn-primary' : 'admin-btn-secondary'}
              onClick={() => setRangeKey(r.key)}
            >{r.label}</button>
          ))}
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} />
          <span>{t.adminShowTestData}</span>
        </label>
      </div>

      {/* Custom date range — two day pickers, applied as soon as both are set. */}
      {rangeKey === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 22 }}>
          <Field label={t.adminFrom}>
            <input type="date" value={custom.from} max={custom.to || todayStr()}
              onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} style={inputStyle} />
          </Field>
          <Field label={t.adminTo}>
            <input type="date" value={custom.to} min={custom.from || undefined} max={todayStr()}
              onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} style={inputStyle} />
          </Field>
          {(!custom.from || !custom.to) && (
            <span style={{ fontSize: '0.85rem', color: '#888' }}>{t.adminSelectDateRange}</span>
          )}
        </div>
      )}

      {/* ── Funnel ── */}
      <section style={{ marginBottom: 28 }}>
        <h2 className="page-title" style={{ fontSize: '1.1rem', marginBottom: 12 }}>{t.adminConversionFunnel}</h2>
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 8, flexWrap: 'wrap' }}>
          <FunnelStep value={funnel.visitors} label={t.adminVisitors} />
          <FunnelArrow pct={funnel.visitorToPreviewPct} />
          <FunnelStep value={funnel.previews} label={t.adminPreviews} />
          <FunnelArrow pct={funnel.previewToPurchasePct} />
          <FunnelStep value={funnel.purchases} label={t.adminPurchases} highlight />
        </div>
        <p style={{ color: '#888', fontSize: '0.85rem', marginTop: 10 }}>
          {t.adminOverallConversion} <strong>{funnel.overallPct}%</strong>
        </p>
        <p style={{ color: '#888', fontSize: '0.85rem', marginTop: 6 }}>
          {t.adminBannerClicks} <strong>{funnel.promoClicks ?? 0}</strong>
          {' '}({funnel.promoClickers ?? 0} {t.adminUniqueVisitor} · {funnel.promoClickPct ?? 0}% {t.adminOfVisitors})
        </p>
        {Array.isArray(funnel.promoByBanner) && funnel.promoByBanner.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {funnel.promoByBanner.map((b) => (
              <li
                key={b.slug}
                style={{
                  fontSize: '0.82rem', color: '#555', background: '#f4f1fb',
                  border: '1px solid #e7e0f5', borderRadius: 999, padding: '4px 12px',
                }}
              >
                {bannerLabel(b.slug, b.name)}: <strong>{b.clicks}</strong>
                {' '}<span style={{ color: '#999' }}>({b.clickers} {t.adminVisitor})</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Financial summary ── */}
      <section style={{ marginBottom: 28 }}>
        <h2 className="page-title" style={{ fontSize: '1.1rem', marginBottom: 12 }}>{t.adminFinancialPerformance}</h2>
        <div className="admin-stats-grid">
          <StatCard value={money(tot.incomeCents, t.currency)} label={t.adminRevenueLabel} color="#166534" />
          <StatCard value={money(tot.cogsCents, t.currency)} label={t.adminCogsCost} color="#b45309" />
          <StatCard value={money(tot.expenseCents, t.currency)} label={t.adminExpensesAds} color="#b45309" />
          <StatCard value={money(tot.profitCents, t.currency)} label={t.adminNetProfit} color={tot.profitCents >= 0 ? '#166534' : '#b91c1c'} />
          <StatCard value={`${tot.marginPct}%`} label={t.adminProfitMargin} />
          <StatCard value={money(tot.aiSpendCents, t.currency)} label={t.adminAiSpendActual} />
        </div>

        {/* Daily income vs spend bars */}
        {activeDays.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <h3 style={{ fontSize: '0.95rem', color: '#555', marginBottom: 8 }}>{t.adminDailyIncomeSpend}</h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, overflowX: 'auto', height: 130, padding: '4px 0', borderBottom: '1px solid #eee' }}>
              {financial.dailySeries.map((d) => {
                const spend = d.cogsCents + d.expenseCents;
                return (
                  <div key={d.date} title={`${fmtDay(d.date)} — ${t.adminDayTooltipIncome} ${money(d.incomeCents, t.currency)} / ${t.adminDayTooltipSpend} ${money(spend, t.currency)} / ${t.adminDayTooltipProfit} ${money(d.profitCents, t.currency)}`}
                    style={{ display: 'flex', flexDirection: 'column-reverse', alignItems: 'center', gap: 2, minWidth: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 110 }}>
                      <div style={{ width: 5, height: `${Math.max(0, (d.incomeCents / maxDay) * 110)}px`, background: '#22c55e', borderRadius: '2px 2px 0 0' }} />
                      <div style={{ width: 5, height: `${Math.max(0, (spend / maxDay) * 110)}px`, background: '#f97316', borderRadius: '2px 2px 0 0' }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: '0.8rem', color: '#666' }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#22c55e', borderRadius: 2, marginInlineEnd: 4 }} />{t.adminLegendIncome}</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#f97316', borderRadius: 2, marginInlineEnd: 4 }} />{t.adminLegendSpend}</span>
            </div>
          </div>
        )}
      </section>

      {/* ── COGS rates ── */}
      {rates && (
        <section style={{ marginBottom: 28 }}>
          <h2 className="page-title" style={{ fontSize: '1.1rem', marginBottom: 12 }}>{t.adminCostRates}</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <RateField label={t.adminBookPrinting} value={rates.printingCents / 100}
              onChange={(v) => setRates((r) => ({ ...r, printingCents: Math.round(v * 100) }))} />
            <RateField label={t.adminDeliveryCost} value={rates.deliveryCents / 100}
              onChange={(v) => setRates((r) => ({ ...r, deliveryCents: Math.round(v * 100) }))} />
            <RateField label={t.adminExchangeRate} step="0.01" value={rates.aiUsdToIls}
              onChange={(v) => setRates((r) => ({ ...r, aiUsdToIls: v }))} />
            <button type="button" className="admin-btn-primary" onClick={saveRates} disabled={savingRates}>
              {savingRates ? '…' : t.adminSaveRates}
            </button>
            {ratesMsg && <span style={{ fontSize: '0.85rem', color: ratesMsg === t.adminSaved ? '#166534' : '#b91c1c' }}>{ratesMsg}</span>}
          </div>
          <p style={{ color: '#888', fontSize: '0.8rem', marginTop: 8 }}>
            {t.adminAiCostNote}
          </p>
        </section>
      )}

      {/* ── Expenses ── */}
      <section style={{ marginBottom: 28 }}>
        <h2 className="page-title" style={{ fontSize: '1.1rem', marginBottom: 12 }}>{t.adminExpenses}</h2>
        <form onSubmit={submitExpense} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
          <Field label={t.adminDescription}>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required
              placeholder={t.adminDescriptionPlaceholder} style={inputStyle} />
          </Field>
          <Field label={t.adminType}>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={inputStyle}>
              {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label={t.adminAmount}>
            <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required style={{ ...inputStyle, width: 110 }} />
          </Field>
          <Field label={t.adminPeriod}>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} style={inputStyle}>
              <option value="period">{t.adminPeriodSpread}</option>
              <option value="one_time">{t.adminOneTime}</option>
            </select>
          </Field>
          {form.kind === 'one_time' ? (
            <Field label={t.adminDate}>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required style={inputStyle} />
            </Field>
          ) : (
            <>
              <Field label={t.adminFrom}>
                <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} required style={inputStyle} />
              </Field>
              <Field label={t.adminTo}>
                <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} required style={inputStyle} />
              </Field>
            </>
          )}
          <button type="submit" className="admin-btn-primary" disabled={savingExpense}>
            {savingExpense ? '…' : t.adminAdd}
          </button>
          {expenseError && <span style={{ fontSize: '0.85rem', color: '#b91c1c' }}>{expenseError}</span>}
        </form>

        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ textAlign: 'right', color: '#888' }}>
                <th style={{ padding: '8px 10px' }}>{t.adminDescription}</th>
                <th style={{ padding: '8px 10px' }}>{t.adminType}</th>
                <th style={{ padding: '8px 10px' }}>{t.adminAmountCol}</th>
                <th style={{ padding: '8px 10px' }}>{t.adminPeriod}</th>
                <th style={{ padding: '8px 10px' }} />
              </tr>
            </thead>
            <tbody>
              {expenses.map((ex) => (
                <tr key={ex._id} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: '8px 10px' }}>{ex.label}</td>
                  <td style={{ padding: '8px 10px' }}>{CATEGORY_AR[ex.category] || ex.category}</td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{money(ex.amountCents, t.currency)}</td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    {ex.kind === 'one_time' ? fmtDay(ex.date) : `${fmtDay(ex.startDate)} – ${fmtDay(ex.endDate)}`}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <button type="button" className="admin-link-btn admin-link-danger" onClick={() => deleteExpense(ex._id)}>{t.adminDeleteExpense}</button>
                  </td>
                </tr>
              ))}
              {expenses.length === 0 && (
                <tr><td colSpan={5} style={{ padding: '16px', textAlign: 'center', color: '#888' }}>{t.adminNoExpensesYet}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Email tracking ── */}
      <EmailTrackingSection />

      {/* ── Engagement ── */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
        <RankTable title={t.adminTopViewedStories} rows={topTemplatesViewed} valueKey="views" valueLabel={t.adminViewCount} />
        <RankTable title={t.adminTopPreviewed} rows={topTemplatesPreviewed} valueKey="previews" valueLabel={t.adminPreviewCount} />
        <RankTable title={t.adminTopViewedCategories} rows={topCategories} valueKey="views" valueLabel={t.adminViewCount} />
      </section>
    </div>
  );
}

function EmailTrackingSection() {
  const t = useT();

  const EMAIL_TYPE_LABELS = {
    order_confirmation: t.adminEmailTypeOrderConfirmation,
    story_ready: t.adminEmailTypeStoryReady,
    story_pdf: t.adminEmailTypeStoryPdf,
    book_ready: t.adminEmailTypeBookReady,
    teaser: t.adminEmailTypeTeaser,
    story_finalize: t.adminEmailTypeStoryFinalize,
    account_created: t.adminEmailTypeAccountCreated,
    magic_login: t.adminEmailTypeMagicLogin,
    password_reset: t.adminEmailTypePasswordReset,
    payment_notice: t.adminEmailTypePaymentNotice,
    unknown: t.adminEmailTypeUnknown,
  };

  const EMAIL_FILTERS = [
    { value: '', label: t.adminEmailFilterAll },
    { value: 'teaser', label: t.adminEmailTypeTeaser },
    { value: 'order_confirmation', label: t.adminEmailTypeOrderConfirmation },
    { value: 'book_ready', label: t.adminEmailTypeBookReady },
    { value: 'story_finalize', label: t.adminEmailTypeStoryFinalize },
    { value: 'account_created', label: t.adminEmailTypeAccountCreated },
    { value: 'story_pdf', label: t.adminEmailTypeStoryPdf },
    { value: 'magic_login', label: t.adminEmailTypeMagicLogin },
    { value: 'password_reset', label: t.adminEmailTypePasswordReset },
    { value: 'payment_notice', label: t.adminPaymentNoticeShort },
  ];

  const OPENED_FILTERS = [
    { value: '', label: t.adminEmailFilterAll },
    { value: 'true', label: t.adminEmailFilterOpened },
    { value: 'false', label: t.adminEmailFilterNotOpened },
  ];

  const [emails, setEmails] = useState({ rows: [], total: 0 });
  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [openedFilter, setOpenedFilter] = useState('');
  const PAGE_SIZE = 20;

  const load = useCallback(() => {
    const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
    if (typeFilter) params.set('type', typeFilter);
    if (openedFilter) params.set('opened', openedFilter);
    api.get(`/api/admin/emails?${params}`).then(setEmails).catch(() => {});
  }, [page, typeFilter, openedFilter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil((emails.total || 0) / PAGE_SIZE);

  return (
    <section style={{ marginBottom: 28 }}>
      <h2 className="page-title" style={{ fontSize: '1.1rem', marginBottom: 12 }}>{t.adminEmailTracking}</h2>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12, alignItems: 'flex-end' }}>
        <Field label={t.adminType}>
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }} style={inputStyle}>
            {EMAIL_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </Field>
        <Field label={t.adminStatus}>
          <select value={openedFilter} onChange={(e) => { setOpenedFilter(e.target.value); setPage(0); }} style={inputStyle}>
            {OPENED_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </Field>
        <span style={{ fontSize: '0.85rem', color: '#888', paddingBottom: 8 }}>{emails.total} {t.adminEmailCount}</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ textAlign: 'right', color: '#888' }}>
              <th style={{ padding: '8px 10px' }}>{t.adminRecipient}</th>
              <th style={{ padding: '8px 10px' }}>{t.adminType}</th>
              <th style={{ padding: '8px 10px' }}>{t.adminSubject}</th>
              <th style={{ padding: '8px 10px' }}>{t.adminSent}</th>
              <th style={{ padding: '8px 10px', textAlign: 'center' }}>{t.adminOpenedQuestion}</th>
              <th style={{ padding: '8px 10px' }}>{t.adminOpenTime}</th>
              <th style={{ padding: '8px 10px', textAlign: 'center' }}>{t.adminOpenCount}</th>
            </tr>
          </thead>
          <tbody>
            {emails.rows.map((e) => (
              <tr key={e._id} style={{ borderTop: '1px solid #eee' }}>
                <td style={{ padding: '8px 10px', direction: 'ltr', textAlign: 'right' }}>{e.to}</td>
                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: '0.8rem',
                    background: e.type === 'teaser' ? '#fef3c7' : '#f0fdf4',
                    color: e.type === 'teaser' ? '#92400e' : '#166534',
                    border: `1px solid ${e.type === 'teaser' ? '#fde68a' : '#bbf7d0'}`,
                  }}>
                    {EMAIL_TYPE_LABELS[e.type] || e.type}
                  </span>
                </td>
                <td style={{ padding: '8px 10px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subject}</td>
                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', direction: 'ltr', textAlign: 'right' }}>
                  {new Date(e.createdAt).toLocaleDateString('ar', { day: 'numeric', month: 'short' })}
                  {' '}
                  <span style={{ color: '#999' }}>{new Date(e.createdAt).toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })}</span>
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                  {e.openedAt
                    ? <span style={{ color: '#16a34a', fontWeight: 700 }}>{t.adminYes}</span>
                    : <span style={{ color: '#dc2626' }}>{t.adminNo}</span>}
                </td>
                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', direction: 'ltr', textAlign: 'right' }}>
                  {e.openedAt
                    ? <>
                        {new Date(e.openedAt).toLocaleDateString('ar', { day: 'numeric', month: 'short' })}
                        {' '}
                        <span style={{ color: '#999' }}>{new Date(e.openedAt).toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })}</span>
                      </>
                    : <span style={{ color: '#ccc' }}>—</span>}
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>{e.openCount || 0}</td>
              </tr>
            ))}
            {emails.rows.length === 0 && (
              <tr><td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#888' }}>{t.adminNoEmailsYet}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
          <button type="button" className="admin-btn-secondary" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>{t.adminPrevPage}</button>
          <span style={{ fontSize: '0.85rem', color: '#666', padding: '8px 4px' }}>{page + 1} / {totalPages}</span>
          <button type="button" className="admin-btn-secondary" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>{t.adminNextPage}</button>
        </div>
      )}
    </section>
  );
}

const inputStyle = { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: '0.9rem' };

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8rem', color: '#666' }}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function RateField({ label, value, onChange, step = '1' }) {
  return (
    <Field label={label}>
      <input type="number" min="0" step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)} style={{ ...inputStyle, width: 130 }} />
    </Field>
  );
}

function StatCard({ value, label, color }) {
  return (
    <div className="admin-stat-card">
      <span className="admin-stat-value" style={color ? { color } : undefined}>{value}</span>
      <span className="admin-stat-label">{label}</span>
    </div>
  );
}

function FunnelStep({ value, label, highlight }) {
  return (
    <div className="admin-stat-card" style={{ flex: '1 1 140px', minWidth: 120, ...(highlight ? { borderColor: '#22c55e', borderWidth: 2, borderStyle: 'solid' } : {}) }}>
      <span className="admin-stat-value">{(value || 0).toLocaleString('en')}</span>
      <span className="admin-stat-label">{label}</span>
    </div>
  );
}

function FunnelArrow({ pct }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 56, color: '#888' }}>
      <span style={{ fontSize: '1.2rem' }}>←</span>
      <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{pct}%</span>
    </div>
  );
}

function RankTable({ title, rows, valueKey, valueLabel }) {
  const t = useT();
  return (
    <div>
      <h3 style={{ fontSize: '0.95rem', color: '#555', marginBottom: 8 }}>{title}</h3>
      <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ textAlign: 'right', color: '#888' }}>
            <th style={{ padding: '6px 10px' }}>#</th>
            <th style={{ padding: '6px 10px' }}>{t.adminName}</th>
            <th style={{ padding: '6px 10px' }}>{valueLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.slug || i} style={{ borderTop: '1px solid #eee' }}>
              <td style={{ padding: '6px 10px', color: '#999' }}>{i + 1}</td>
              <td style={{ padding: '6px 10px' }}>{r.name}</td>
              <td style={{ padding: '6px 10px', fontWeight: 600 }}>{(r[valueKey] || 0).toLocaleString('en')}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={3} style={{ padding: '14px', textAlign: 'center', color: '#888' }}>{t.adminNoDataYet}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
